import { Injectable , inject} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConfigService } from 'app/core/config/app-config.service';

export interface DynamicConfig {
  client: string;
  wpurl: string;
  wpu: string;
  wpt: string;
  // Add other config properties as needed
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class DynamicConfigService {
  private config: DynamicConfig | null = null;
  private configSubject = new BehaviorSubject<DynamicConfig | null>(null);
  public config$ = this.configSubject.asObservable();
  private readonly CONFIG_STORAGE_KEY = 'studema_dynamic_config';
  private readonly CONFIG_EXPIRY_KEY = 'studema_config_expiry';
  /** Key-server code the cached config was loaded for. */
  private readonly CONFIG_OWNER_KEY = 'studema_config_owner';
  private fallbackFor: string | null = null;
  private fallbackConfig: DynamicConfig | null = null;
  private readonly CONFIG_EXPIRY_HOURS = 24; // Cache for 24 hours

  constructor(private http: HttpClient, readonly appConfig: AppConfigService) {
    // Try to load from cache on service initialization
    this.loadFromCache();
  }

  /**
   * Load configuration from server or cache
   */
  async loadConfig(): Promise<DynamicConfig> {
    const { primaryCode, clientKeyCode } = this.getKeyCodes();

    // The cache is per browser, not per user: only reuse it for the same company code,
    // otherwise another user's WordPress credentials would be sent.
    if (this.config && this.isConfigValid() && this.getCacheOwner() === primaryCode) {
      return this.config;
    }

    const cachedConfig = this.getCachedConfig();
    if (cachedConfig && this.isConfigValid() && this.getCacheOwner() === primaryCode) {
      this.config = cachedConfig;
      this.configSubject.next(this.config);
      return this.config;
    }
    this.config = null;

    // Not persisted, so a code registered on the key server later is picked up after a reload.
    if (this.fallbackFor === primaryCode && this.fallbackConfig) {
      return this.fallbackConfig;
    }

    // Load from key server (api-kserver.php): codes must exist on that server or it returns { status: "error" } with no data.
    try {
      const codesToTry = primaryCode === clientKeyCode ? [primaryCode] : [primaryCode, clientKeyCode];

      for (const code of codesToTry) {
        const url = `${this.appConfig.get('keyServer')}?code=${encodeURIComponent(code)}`;
        console.log('DynamicConfig: loading from', url);

        const response = await this.http.get<any>(url).pipe(
          catchError((err) => {
            console.warn('DynamicConfig: HTTP error for', url, err?.message ?? err);
            return of(null);
          }),
        ).toPromise();

        const st = response?.status != null ? String(response.status).toLowerCase() : '';
        if (st === 'error') {
          console.warn(
            'DynamicConfig: key server has no config for code (register this code on the key server, or rely on fallback):',
            code,
            response?.message ?? '',
          );
        }

        const parsed = this.parseKeyServerResponse(response);
        if (parsed) {
          this.config = parsed;
          this.saveToCache(this.config, primaryCode);
          this.configSubject.next(this.config);
          console.log('DynamicConfig: loaded successfully for code', code);
          return this.config;
        }
      }

      console.warn(
        'DynamicConfig: no valid response for codes',
        codesToTry.join(', '),
        '- using environment fallback.',
      );
    } catch (error) {
      console.error('Failed to load dynamic config, using fallback:', error);
    }
    this.fallbackFor = primaryCode;
    this.fallbackConfig = this.getFallbackConfig();
    return this.fallbackConfig;
  }

  /** Key-server code for the logged-in user's company, plus the client-wide code. */
  private getKeyCodes(): { primaryCode: string, clientKeyCode: string } {
    const { companySlug, companyId } = this.getAuthCompanyMeta();
    const clientKeyCode = `st-${this.appConfig.get<string>('client', '')}`;
    const primaryCode = companySlug && companyId ? `st-${companySlug}-${companyId}` : clientKeyCode;
    return { primaryCode, clientKeyCode };
  }

  private getCacheOwner(): string | null {
    return localStorage.getItem(this.CONFIG_OWNER_KEY);
  }

  /**
   * Normalizes key-server JSON into DynamicConfig (supports success/OK, data/config wrapper, or bare wpurl object).
   */
  private parseKeyServerResponse(response: any): DynamicConfig | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const statusRaw = response.status;
    const status = typeof statusRaw === 'string' ? statusRaw.toLowerCase() : '';
    const wrappedOk = status === 'success' || status === 'ok';
    if (wrappedOk) {
      const payload = response.data ?? response.config;
      if (payload && typeof payload === 'object' && typeof payload.wpurl === 'string' && payload.wpurl.length > 0) {
        return this.normalizeLoadedConfig(payload);
      }
    }

    if (typeof response.wpurl === 'string' && response.wpurl.length > 0) {
      return this.normalizeLoadedConfig(response);
    }

    return null;
  }

  private normalizeLoadedConfig(raw: any): DynamicConfig {
    return {
      ...raw,
      client: raw.client ?? this.appConfig.get('client', ''),
      wpurl: typeof raw.wpurl === 'string' ? raw.wpurl : '',
      wpu: typeof raw.wpu === 'string' ? raw.wpu : '',
      wpt: typeof raw.wpt === 'string' ? raw.wpt : '',
    };
  }

  /**
   * Extract company metadata from local storage auth payloads
   */
  private getAuthCompanyMeta(): { companySlug: string | null, companyId: string | null } {
    try {
      const possibleKeys = ['user', 'CURRENT_USER', 'auth', 'authData', 'app_user'];
      let payload: any = null;
      let foundKey: string | null = null;

      for (const key of possibleKeys) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const json = JSON.parse(raw);
          if (json) { 
            payload = json; 
            foundKey = key;
            break; 
          }
        } catch { /* ignore parse errors */ }
      }

      console.log('DynamicConfig: Found auth payload in key:', foundKey);

      if (!payload) {
        console.log('DynamicConfig: No payload found, returning null');
        return { companySlug: null, companyId: null };
      }

      // Some apps store under { data: {...} }
      const data = payload.data ?? payload;

      // Also check nested user object (your auth structure has user.company_id and user.company)
      const user = data.user ?? {};

      // Resolved at login so users without a company of their own use the system default.
      const clearwordName = data.clearword_company ?? user.clearword_company;
      const clearwordId = data.clearword_company_id ?? user.clearword_company_id;
      const hasClearwordCompany = !!clearwordName && !!clearwordId;

      const companyName: string | undefined = hasClearwordCompany ? clearwordName :
          data.company ?? data.companyname ?? data.company_name ?? 
          user.company ?? user.companyname ?? user.company_name;
          
      const companyId: string | number | undefined = hasClearwordCompany ? clearwordId :
          data.company_id ?? data.companyId ?? data.companyid ?? 
          user.company_id ?? user.companyId ?? user.companyid;

      console.log('DynamicConfig: Extracted companyName:', companyName);
      console.log('DynamicConfig: Extracted companyId:', companyId);

      if (!companyName || !companyId) {
        console.log('DynamicConfig: Missing companyName or companyId, returning null');
        return { companySlug: null, companyId: null };
      }

      const slug = this.sanitizeCompanyName(String(companyName));
      console.log('DynamicConfig: Generated slug from companyName:', slug);
      console.log('DynamicConfig: Final result:', { companySlug: slug, companyId: String(companyId) });
      
      return { companySlug: slug, companyId: String(companyId) };
    } catch (error) {
      console.log('DynamicConfig: Error in getAuthCompanyMeta:', error);
      return { companySlug: null, companyId: null };
    }
  }

  /**
   * Sanitize company name to URL-safe slug (remove diacritics, non-alnum to '-')
   */
  private sanitizeCompanyName(name: string): string {
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip accents
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  /**
   * Get WordPress configuration
   */
  async getWordpressConfig(): Promise<{wpurl: string, wpu: string, wpt: string}> {
    const config = await this.loadConfig();
    return {
      wpurl: config.wpurl,
      wpu: config.wpu,
      wpt: config.wpt
    };
  }

  /**
   * Get specific configuration value
   */
  async getConfigValue(key: string): Promise<any> {
    const config = await this.loadConfig();
    return config[key];
  }

  /**
   * Check if current config is still valid (not expired)
   */
  private isConfigValid(): boolean {
    const expiry = localStorage.getItem(this.CONFIG_EXPIRY_KEY);
    if (!expiry) return false;
    
    const expiryTime = parseInt(expiry);
    const now = Date.now();
    return now < expiryTime;
  }

  /**
   * Get cached config from localStorage
   */
  private getCachedConfig(): DynamicConfig | null {
    try {
      const cached = localStorage.getItem(this.CONFIG_STORAGE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Failed to parse cached config:', error);
      return null;
    }
  }

  /**
   * Save config to localStorage with expiry
   */
  private saveToCache(config: DynamicConfig, owner: string): void {
    try {
      localStorage.setItem(this.CONFIG_STORAGE_KEY, JSON.stringify(config));
      localStorage.setItem(this.CONFIG_OWNER_KEY, owner);
      const expiryTime = Date.now() + (this.CONFIG_EXPIRY_HOURS * 60 * 60 * 1000);
      localStorage.setItem(this.CONFIG_EXPIRY_KEY, expiryTime.toString());
    } catch (error) {
      console.error('Failed to save config to cache:', error);
    }
  }

  /**
   * Load config from cache on service initialization
   */
  private loadFromCache(): void {
    const cachedConfig = this.getCachedConfig();
    if (cachedConfig && this.isConfigValid() && this.getCacheOwner() === this.getKeyCodes().primaryCode) {
      this.config = cachedConfig;
      this.configSubject.next(this.config);
    }
  }

  /**
   * Get fallback configuration from environment
   */
  private getFallbackConfig(): DynamicConfig {
    const wpurl = this.appConfig.get<string>('wpurl', '') || this.appConfig.get<string>('fallback_wpurl', '');
    const wpu = this.appConfig.get<string>('wpu', '');
    const wpt = this.appConfig.get<string>('wpt', '');
    console.log('Using fallback configuration from config.json');
    return {
      client: this.appConfig.get<string>('client', ''),
      wpurl,
      wpu,
      wpt,
    };
  }

  /**
   * Clear cached configuration
   */
  clearCache(): void {
    localStorage.removeItem(this.CONFIG_STORAGE_KEY);
    localStorage.removeItem(this.CONFIG_EXPIRY_KEY);
    localStorage.removeItem(this.CONFIG_OWNER_KEY);
    this.config = null;
    this.fallbackFor = null;
    this.fallbackConfig = null;
    this.configSubject.next(null);
  }

  /**
   * Force reload configuration from server
   */
  async reloadConfig(): Promise<DynamicConfig> {
    this.clearCache();
    return this.loadConfig();
  }
}
